import { Amplify } from 'aws-amplify'
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut
} from 'aws-amplify/auth'
import type { AuthProvider } from './auth-provider'
import type { AuthSession } from './auth.types'

/**
 * Auth provider backed by AWS Amplify talking to AWS Cognito.
 *
 * Alternative to OidcAuthProvider. Amplify handles token refresh and the
 * OAuth callback automatically via its internal Hub listener.
 */
export class AmplifyAuthProvider implements AuthProvider {
  init(): void {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
          userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          loginWith: {
            oauth: {
              domain: import.meta.env.VITE_COGNITO_DOMAIN,
              scopes: ['email', 'openid', 'profile'],
              redirectSignIn: [`${window.location.origin}/adx/auth/callback`],
              redirectSignOut: [`${window.location.origin}/admin/login`],
              responseType: 'code',
              providers: [
                { custom: import.meta.env.VITE_COGNITO_PROVIDER_NAME }
              ]
            },
          }
        }
      }
    })
  }

  async signInWithRedirect(): Promise<void> {
    await signInWithRedirect({
      provider: { custom: import.meta.env.VITE_COGNITO_PROVIDER_NAME }
    })
  }

  async handleCallback(): Promise<AuthSession | null> {
    // Amplify processes the OAuth callback automatically on page load via Hub.
    // We just read the resulting session.
    return this.getSession()
  }

  async getSession(): Promise<AuthSession | null> {
    try {
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken
      const accessToken = session.tokens?.accessToken
      if (!idToken || !accessToken) return null

      const claims = idToken.payload
      return {
        idToken: idToken.toString(),
        accessToken: accessToken.toString(),
        user: {
          id: Number(claims.sub) || 0,
          email: (claims.email as string) ?? '',
          name: (claims.name as string) ?? (claims.email as string) ?? ''
        }
      }
    } catch {
      return null
    }
  }

  async getAccessToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString()
    } catch {
      return undefined
    }
  }

  async signOut(): Promise<void> {
    await signOut()
  }
}
